import { useCallback, useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import Cookies from "js-cookie";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRequest } from "@/utils/request";
import { useNotificationStore } from "@/components/context/notification-store";

const POLLING_INTERVAL_MS =
  Number(process.env.NEXT_PUBLIC_NOTIFICATION_POLLING_INTERVAL_MS) || 60_000;
const BROADCAST_CHANNEL_NAME = "offerz-notifications";
const STORAGE_COUNT_KEY = "offerz-unread-count";
const STORAGE_LAST_SEEN_ID_KEY = "offerz-last-notification-id";

export interface NotificationPayload {
  title: string;
  message: string;
}

export interface UseNotificationSyncOptions {
  onNotification: (payload: NotificationPayload) => void;
}

export function useNotificationSync({ onNotification }: UseNotificationSyncOptions): void {
  const { status } = useSession();
  const queryClient = useQueryClient();
  const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);
  const setIsCountLoading = useNotificationStore((s) => s.setIsCountLoading);
  const reset = useNotificationStore((s) => s.reset);

  const lastSeenIdRef = useRef<string | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const onNotificationRef = useRef(onNotification);
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  const lang = Cookies.get("locale") || "de";

  const applyCountLocally = useCallback(
    (count: number) => {
      setUnreadCount(count);
      setIsCountLoading(false);
      try {
        localStorage.setItem(STORAGE_COUNT_KEY, String(count));
      } catch {
        // no-op
      }
    },
    [setIsCountLoading, setUnreadCount],
  );

  const query = useQuery({
    queryKey: ["notifications-poll"],
    queryFn: () =>
      getRequest({
        path: { name: "notificationsPoll" },
        lang,
        skipCache: true,
      }),
    enabled: status === "authenticated",
    refetchInterval: POLLING_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (status !== "authenticated") return;
    if (!query.isSuccess) return;

    const response = query.data;
    if (!response?.success) {
      setIsCountLoading(false);
      return;
    }

    const data = response.data;
    const notification = data?.notification;
    const id = notification?.id != null ? String(notification.id) : null;

    const unreadCount = data?.unread_count;
    if (typeof unreadCount === "number") {
      applyCountLocally(unreadCount);
    } else {
      setIsCountLoading(false);
    }

    if (!id || id === lastSeenIdRef.current) return;

    lastSeenIdRef.current = id;
    try {
      localStorage.setItem(STORAGE_LAST_SEEN_ID_KEY, id);
    } catch {
      // no-op
    }
    queryClient.invalidateQueries({ queryKey: ["notifications"] });

    channelRef.current?.postMessage({
      type: "notification",
      unreadCount: typeof unreadCount === "number" ? unreadCount : undefined,
      title: notification?.title,
      description: notification?.description,
      id,
    });

    const title = notification?.title;
    const description = notification?.description;
    if (title || description) {
      onNotificationRef.current({
        title: String(title || ""),
        message: String(description || ""),
      });
    }
  }, [
    query.dataUpdatedAt,
    query.isSuccess,
    status,
    applyCountLocally,
    setIsCountLoading,
    queryClient,
  ]);

  useEffect(() => {
    if (status !== "authenticated") {
      queryClient.removeQueries({ queryKey: ["notifications-poll"] });
      try {
        localStorage.removeItem(STORAGE_COUNT_KEY);
        localStorage.removeItem(STORAGE_LAST_SEEN_ID_KEY);
      } catch {
        // no-op
      }
      reset();
      lastSeenIdRef.current = null;
      return;
    }

    try {
      const savedId = localStorage.getItem(STORAGE_LAST_SEEN_ID_KEY);
      if (savedId) lastSeenIdRef.current = savedId;
    } catch {
      // no-op
    }

    try {
      const cached = localStorage.getItem(STORAGE_COUNT_KEY);
      if (cached != null) {
        const parsed = Number(cached);
        if (Number.isFinite(parsed)) {
          setUnreadCount(parsed);
          setIsCountLoading(false);
          return;
        }
      }
    } catch {
      // no-op
    }
    setIsCountLoading(true);
  }, [status, reset, setUnreadCount, setIsCountLoading, queryClient]);

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent) => {
      const msg = event.data;
      if (msg.type === "notification") {
        if (typeof msg.unreadCount === "number") {
          applyCountLocally(msg.unreadCount);
        }
        if (!msg.id || String(msg.id) === lastSeenIdRef.current) return;
        lastSeenIdRef.current = String(msg.id);
        const title = msg.title;
        const description = msg.description;
        if (title || description) {
          onNotificationRef.current({
            title: String(title || ""),
            message: String(description || ""),
          });
        }
      } else if (msg.type === "count") {
        if (typeof msg.unreadCount === "number") {
          applyCountLocally(msg.unreadCount);
        }
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [applyCountLocally]);
}
